import * as coreEntities from 'core/store/index';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import analyticsService from '../services/analyticsService';

/**
 * Custom hook for analytics functionality
 * Provides easy access to analytics methods with automatic user and context data
 */
function useAnalytics() {
  // Get user information from Redux store
  const user = useSelector((state) => state.auth?.user);

  // Get tenant from organization endpoint (stored in Redux)
  const tenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));

  /**
   * Get user data formatted for analytics SDK
   * @returns {Object|null} Formatted user data or null if no user
   */
  const getUserData = useCallback(() => {
    if (!user) return null;
    console.log('user', user);

    return {
      id: user.guid || user.id || user.user_id,
      role_guid: user.role_guid,
      school_state_guid: user.school_state_guid,
      school_city_guid: user.school_city_guid,
      school_guid: user.school_guid,
      school_name: user.school_name,
      is_tangerine_ai: user.is_tangerine_ai === 1,
      language: user.language || 'es',
    };
  }, [user]);

  /**
   * Generate or retrieve device ID
   * @returns {string} Device ID
   */
  const getDeviceId = useCallback(() => {
    const storedId = localStorage.getItem('device_id');
    if (storedId) return storedId;

    const newId = 'device_' + Math.random().toString(36).substring(2, 11);
    localStorage.setItem('device_id', newId);
    return newId;
  }, []);

  /**
   * Detect device type
   * @returns {string} Device type
   */
  const getDeviceType = useCallback(() => {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile ? 'mobile' : 'web';
  }, []);

  /**
   * Detect operating system
   * @returns {string} Operating system
   */
  const getOS = useCallback(() => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }, []);

  /**
   * Get platform (modern API with fallback)
   * @returns {string} Platform
   */
  const getPlatform = useCallback(() => {
    if (navigator.userAgentData?.platform) {
      return navigator.userAgentData.platform;
    }
    return getOS();
  }, [getOS]);

  /**
   * Get context data formatted for analytics SDK
   * @returns {Object} Formatted context data
   */
  const getContextData = useCallback(() => {
    return {
      device_id: getDeviceId(),
      device_type: getDeviceType(),
      os: getOS(),
      platform: getPlatform(),
      browser: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language || 'es',
      library: '@oneclick/analytics-sdk',
    };
  }, [getDeviceId, getDeviceType, getOS, getPlatform]);

  /**
   * Track an analytics event
   * @param {EventType} eventType - Type of event from EventType enum
   * @param {Object} [payload] - Payload data
   * @returns {Promise<Object|null>} Response from analytics service or null if failed
   */
  const trackEvent = useCallback(
    async (eventType, payload = {}) => {
      // Get the tenant name and transform it to kebab-case
      const tenantName = tenant?.name?.toLowerCase().replace(/ /g, '-');
      if (!analyticsService.getInitializationStatus()) {
        console.warn('AnalyticsService not initialized');
        return null;
      }

      const userData = getUserData();
      if (!userData) {
        console.warn('No user data available for analytics event');
        return null;
      }

      if (!tenantName) {
        console.warn('No tenant configured for analytics event');
        return null;
      }

      return await analyticsService.trackEvent(eventType, {
        tenant: tenantName,
        user: userData,
        context: getContextData(),
        payload,
      });
    },
    [getUserData, getContextData, tenant]
  );

  return {
    trackEvent,
    isInitialized: analyticsService.getInitializationStatus(),
  };
}

export default useAnalytics;
